// core/store/user.store.ts
import { Injectable, computed, inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  withProps,
  patchState,
} from '@ngrx/signals';
import type { AuthSession } from '@monorepo/types';
import { AuthApi } from '../../features/auth/data/auth-api';
import { RBAC_PERMISSION_CLUSTERS } from '../../features/access-control/models/permission-cluster.model';
import { AUTH_ENABLED } from '../auth/auth.config';
import { Auth } from '../auth/auth';
import { refreshAccessToken } from '../auth/refresh-session';

const initialState = {
  user: null as AuthSession | null,
  accessToken: null as string | null,
  loading: false,
  error: null as string | null,
};

/**
 * Used in place of a real session while AUTH_ENABLED is false. Granted every known
 * permission — since AUTH_ENABLED already bypasses login entirely, this bypass
 * shouldn't also hide sidebar links or block routes behind the new permission guard.
 */
const STATIC_LOCAL_SESSION: AuthSession = {
  user: { id: 'local-dev-user', name: 'Local Dev', email: 'local.dev@coditas.com' },
  token: 'static-local-token',
  role: 'LOCAL_DEV',
  permissions: Array.from(new Set(RBAC_PERMISSION_CLUSTERS.map((entry) => entry.permissionCode))),
};

@Injectable({ providedIn: 'root' })
export class UserStore extends signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    auth: inject(Auth),
    authApi: inject(AuthApi),
  })),
  withComputed((state) => ({
    isAuthenticated: computed(() => !!state.accessToken()),
    displayName: computed(() => state.user()?.user?.name ?? ''),
    role: computed(() => state.user()?.role ?? null),
    permissions: computed(() => state.user()?.permissions ?? []),
  })),
  withMethods((store) => {
    const clearSession = () => {
      store.auth.clearSession();
      patchState(store, initialState);
    };

    return {
      setSession(user: AuthSession, accessToken: string) {
        patchState(store, { user, accessToken, error: null });
      },
      clearSession,
      // Best-effort: the server-side session is revoked if reachable, but the
      // local session is always cleared regardless so the user isn't stuck.
      async logout(): Promise<void> {
        const refreshToken = store.auth.getRefreshToken();
        if (refreshToken) {
          try {
            await store.authApi.logout(refreshToken);
          } catch (err) {
            console.error('Logout request failed:', err);
          }
        }
        clearSession();
      },
      // Rehydrates from the tokens Auth already persisted at login — there's no
      // /auth/me endpoint on the API to fetch the session from.
      async loadCurrentUser(): Promise<void> {
        patchState(store, { loading: true, error: null });

        if (!AUTH_ENABLED) {
          patchState(store, {
            user: STATIC_LOCAL_SESSION,
            accessToken: STATIC_LOCAL_SESSION.token,
            loading: false,
          });
          return;
        }

        let session = store.auth.getSession();

        // The access token itself may have expired while the refresh token is still
        // good — try to refresh once before giving up on the session.
        if (!session) {
          const refreshed = await refreshAccessToken(store.authApi, store.auth);
          session = refreshed ? store.auth.getSession() : null;
        }

        if (!session) {
          patchState(store, { ...initialState, loading: false });
          return;
        }

        patchState(store, {
          user: session,
          accessToken: session.token,
          loading: false,
        });
      },
    };
  })
) {}
